import React from "react";
import classNames from "classnames";
import { Waypoint } from "react-waypoint";
import { LISTING_DISPLAY_MODE } from "app/constants";
import ProductList from "app/pages/Listing/components/ProductList.jsx";
import ProductFullList from "app/pages/Listing/components/ProductFullList.jsx";
import { RESOLUTION } from "app/pages/.shared/responsive/responsiveReducer";
import {
	pushTagOnGridViewActive,
	pushTagOnListViewActive,
	sendTagOnMediaViewOffers,
} from "app/utils/analytics";
import HeadMetadata from "app/utils/metas/HeadMetadata";
import PropTypes from "prop-types";
import { merchandisingShape, merchandisingTopic, productShape } from "app/utils/propTypes";
import isEqual from "lodash/isEqual";
import get from "lodash/get";
import isEmpty from "lodash/isEmpty";
import "./MerchandisingListing.scss";
import MerchandisingFilters from "app/pages/Merchandising/MerchandisingFilters/MerchandisingFilters";
import ListingActionbar from "app/pages/.shared/ListingActionbar/ListingActionbar";
import CookiePolicyFooterContainer from "app/pages/.shared/CookiePolicyFooter/CookiePolicyFooterContainer";
import IconGoToTopArrow from "app/pages/.shared/static/icons/IconGoToTopArrow";
import { PushNotificationContainer } from "app/pages/.shared/PushNotification/PushNotificationContainer";
import ProgressiveImg from "app/pages/.shared/ProgressiveImg";
import Loader from "app/pages/.shared/Loader";
import MerchandisingSwitcher from "app/pages/Merchandising/MerchandisingSwitcher/MerchandisingSwitcher";
import MerchandisingMedias from "app/pages/Merchandising/Media/MerchandisingMedias";
import { FormattedMessage } from "react-intl";
import Button from "app/pages/.shared/form/Button";
import IconArrowDown from "app/pages/.shared/static/icons/IconArrowDown";
import { scrollToElement } from "app/utils/scroller";
import RelativeLink from "app/pages/.shared/RelativeLink";
import MerchandisingProductsFilterContainer from "app/pages/Merchandising/MerchandisingProductsFilterContainer";

const IMG_DESKTOP_SIZES = {
	medium: { height: 250, width: 1024 },
	large: { height: 250, width: 1024 },
	xlarge: { height: 250, width: 1024 },
};

const MEDIA_IMG_DESKTOP_SIZES = {
	medium: { width: 1440 },
	large: { width: 1440 },
	xlarge: { width: 1440 },
};

const IMG_MOBILE_SIZES = {
	small: {},
	medium: {},
};

class MerchandisingListing extends React.Component {
	constructor(props) {
		super(props);

		const merchandisingTopic = props.merchandisingTopics.find(
			merchandisingTopic => props.activeFilter === merchandisingTopic.filterValue
		);

		const merchandisingPictureData = this.buildMerchandisingPicture(props, merchandisingTopic);

		this.state = {
			showScrollTopButton: false,
			listViewDisplay:
				get(props, "currentMerchandising.displayMode") === LISTING_DISPLAY_MODE.LIST,
			...merchandisingPictureData,
		};

		this.productListRef = React.createRef();
		this.actionBarRef = React.createRef();

		this.scrollToProducts = this.scrollToProducts.bind(this);
		this.toogleListView = this.toogleListView.bind(this);
		this.toogleCardView = this.toogleCardView.bind(this);
		this.showScrollTopButton = this.showScrollTopButton.bind(this);
		this.hideScrollTopButton = this.hideScrollTopButton.bind(this);
		this.updateMerchandisingMainImage = this.updateMerchandisingMainImage.bind(this);
	}

	triggerWindowResize() {
		setTimeout(() => {
			// in IE11, got TypeError: Object doesn't support this action
			if (typeof Event === "function") {
				window.dispatchEvent(new Event("resize"));
			}
		}, 200);
	}

	toogleCardView() {
		this.setState({
			listViewDisplay: false,
		});
		pushTagOnGridViewActive();
	}

	toogleListView() {
		this.setState({
			listViewDisplay: true,
		});
		pushTagOnListViewActive();
	}

	showScrollTopButton({ previousPosition, currentPosition }) {
		if (previousPosition === Waypoint.above && currentPosition === Waypoint.inside) {
			this.setState({ showScrollTopButton: false });
		}
	}

	hideScrollTopButton({ previousPosition, currentPosition }) {
		if (previousPosition === Waypoint.inside && currentPosition === Waypoint.above) {
			this.setState({ showScrollTopButton: true });
		}
	}

	handleScrollTopButtonClick() {
		window.scrollTo(0, 0);
	}

	buildMerchandisingPicture({ resolution, currentMerchandising = {} }, merchandisingTopic) {
		const isLargeResolution =
			resolution === RESOLUTION.LARGE || resolution === RESOLUTION.XLARGE;

		if (merchandisingTopic) {
			return {
				merchandisingPictureUrl: isLargeResolution
					? get(merchandisingTopic, "photoLarge.url")
					: get(merchandisingTopic, "photoSmall.url"),
				merchandisingPictureThumbnail: isLargeResolution
					? get(merchandisingTopic, "photoLarge.thumbnail")
					: get(merchandisingTopic, "photoSmall.thumbnail"),
			};
		}
		return {
			merchandisingPictureUrl: isLargeResolution
				? get(currentMerchandising, "mainPhotoLarge.url")
				: get(currentMerchandising, "mainPhotoSmall.url"),
			merchandisingPictureThumbnail: isLargeResolution
				? get(currentMerchandising, "mainPhotoLarge.thumbnail")
				: get(currentMerchandising, "mainPhotoSmall.thumbnail"),
		};
	}

	updateMerchandisingMainImage(merchandisingTopic) {
		const merchandisingPictureData = this.buildMerchandisingPicture(
			this.props,
			merchandisingTopic
		);

		this.setState(merchandisingPictureData);
	}

	componentDidMount() {
		this.triggerWindowResize(); // permet de s'assurer que le merchandisingSwitcher s'affiche integrablement en hauteur
	}

	componentDidUpdate(previousProps) {
		if (
			!isEqual(previousProps.currentMerchandising, this.props.currentMerchandising) ||
			previousProps.resolution !== this.props.resolution
		) {
			const merchandisingPictureData = this.buildMerchandisingPicture(this.props);

			this.setState({
				listViewDisplay:
					this.props.currentMerchandising &&
					this.props.currentMerchandising.displayMode === LISTING_DISPLAY_MODE.LIST,
				...merchandisingPictureData,
			});
		}
	}

	scrollToProducts() {
		scrollToElement(this.productListRef.current, -100);
		sendTagOnMediaViewOffers();
	}

	render() {
		const {
			productsOfMerchandising,
			resolution,
			currentMerchandising,
			merchandisingTopics,
			partnerCode,
			merchandisings,
			merchandisingMedias,
			fetchMerchandising,
			activeFilter,
			fallbackRedirectUrl,
		} = this.props;

		const scrollTopButtonClass = classNames({
			"icon scroll-top-button": true,
			"scroll-top-button--visible": this.state.showScrollTopButton,
		});

		const isLargeResolution =
			resolution === RESOLUTION.LARGE || resolution === RESOLUTION.XLARGE;

		const hasMedia = currentMerchandising.media;

		const mainPictureClassNames = classNames({
			"listing__merchandising-picture": true,
			"listing__merchandising-picture--media": hasMedia,
		});

		let isMerchandisingExpired = false;

		if (currentMerchandising.endAt) {
			const merchEndDate = new Date(currentMerchandising.endAt);
			const today = new Date();
			isMerchandisingExpired = today > merchEndDate;
		}

		if (isEmpty(currentMerchandising)) {
			return (
				<div className="listing listing--merchandising">
					<div className="listing__loader">
						<Loader />
					</div>
				</div>
			);
		} else if (isMerchandisingExpired) {
			return (
				<div className="listing listing--merchandising">
					<ListingActionbar
						listViewDisplay={this.state.listViewDisplay}
						hideSwitchListingViewButtons={true}
						count={0}
						label={currentMerchandising.label}
					/>

					<div className="listing__container">
						<div className="listing__merchandising-expired">
							<div className="listing__merchandising-expired-message">
								<FormattedMessage id="merchandising.expired.message" />
							</div>
							<RelativeLink
								to={{ pathname: fallbackRedirectUrl }}
								design="primary"
								enableLoading
							>
								<FormattedMessage id="confirmation.leave.button" />
							</RelativeLink>
						</div>
					</div>
				</div>
			);
		}

		return (
			<div className="listing listing--merchandising">
				<HeadMetadata
					page="merch"
					data={{
						label: currentMerchandising.label,
					}}
				/>
				<React.Fragment>
					{(partnerCode === "SSFR" || partnerCode === "EKFR") && (
						<div className="listing__container">
							<MerchandisingSwitcher
								fetchMerchandising={fetchMerchandising}
								currentMerchCode={currentMerchandising.code}
								merchandisings={merchandisings}
								resolution={resolution}
							/>
						</div>
					)}

					{this.state.merchandisingPictureUrl && (
						<div className={mainPictureClassNames}>
							<div className="listing__container">
								{isLargeResolution ? (
									<ProgressiveImg
										sizes={
											hasMedia ? MEDIA_IMG_DESKTOP_SIZES : IMG_DESKTOP_SIZES
										}
										src={this.state.merchandisingPictureUrl}
										thumbnail={this.state.merchandisingPictureThumbnail}
									/>
								) : (
									<ProgressiveImg
										sizes={IMG_MOBILE_SIZES}
										src={this.state.merchandisingPictureUrl}
										thumbnail={this.state.merchandisingPictureThumbnail}
									/>
								)}

								{hasMedia && (
									<div className="listing__merchandising-cta">
										<Button submit={false} onClick={this.scrollToProducts}>
											<FormattedMessage id="merchandising.cta.text" />
											<IconArrowDown
												height={10}
												width={10}
												style={{ marginLeft: 10 }}
											/>
										</Button>
									</div>
								)}
							</div>
						</div>
					)}

					{hasMedia && (
						<div className="listing__merchandising-media">
							<MerchandisingMedias
								resolution={resolution}
								articles={merchandisingMedias}
							/>
						</div>
					)}

					<div ref={this.actionBarRef}>
						<ListingActionbar
							listViewDisplay={this.state.listViewDisplay}
							hideSwitchListingViewButtons={partnerCode === "LRFR"}
							count={productsOfMerchandising.length}
							label={currentMerchandising.label}
							onListButtonClick={this.toogleListView}
							onCardButtonClick={this.toogleCardView}
						/>
					</div>

					<div className="listing__merchandising-products" ref={this.productListRef}>
						<div className="listing__container">
							<Waypoint
								topOffset="-2500px"
								scrollableAncestor={
									typeof window !== "undefined" ? window : undefined
								}
								onEnter={this.showScrollTopButton}
								onLeave={this.hideScrollTopButton}
							/>

							{currentMerchandising.introduction && (
								<div
									className="listing__introduction"
									dangerouslySetInnerHTML={{
										__html: currentMerchandising.introduction,
									}}
								/>
							)}

							{get(currentMerchandising, "displayOptions.showFilters") && (
								<MerchandisingProductsFilterContainer
									asideFilterMenu={true}
									resolution={resolution}
									scrollToListingHeader={() => {
										scrollToElement(this.actionBarRef.current, 0, true);
									}}
								/>
							)}

							{merchandisingTopics.length > 0 && (
								<div className="listing__merchandising-filters">
									<MerchandisingFilters
										activeFilter={activeFilter}
										merchandisingTopics={merchandisingTopics}
										onFilterSelect={this.updateMerchandisingMainImage}
									/>
								</div>
							)}

							<div className="listing__product-list">
								{this.state.listViewDisplay && isLargeResolution ? (
									<ProductFullList products={productsOfMerchandising} />
								) : (
									<ProductList products={productsOfMerchandising} />
								)}
							</div>
						</div>
					</div>
				</React.Fragment>
				<span className={scrollTopButtonClass} onClick={this.handleScrollTopButtonClick}>
					<IconGoToTopArrow width={50} height={50} />
				</span>

				<CookiePolicyFooterContainer />
				<PushNotificationContainer />
			</div>
		);
	}
}

MerchandisingListing.propTypes = {
	activeFilter: PropTypes.string,
	resolution: PropTypes.string,
	productsOfMerchandising: PropTypes.arrayOf(productShape),
	merchandisingMedias: PropTypes.arrayOf(merchandisingShape),
	currentMerchandising: merchandisingShape,
	merchandisings: PropTypes.arrayOf(merchandisingShape),
	merchandisingTopics: PropTypes.arrayOf(merchandisingTopic),
	partnerCode: PropTypes.string,
	fetchMerchandising: PropTypes.func,
	fallbackRedirectUrl: PropTypes.string,
};

MerchandisingListing.defaultProps = {
	merchandisingTopics: [],
	productsOfMerchandising: [],
	currentMerchandising: {},
	merchandisings: [],
	merchandisingMedias: [],
};

export default MerchandisingListing;
